// La recarga automática de créditos de una marca (ADR-2026-10-29-recarga-automatica-de-marca): el
// recuadro que la muestra y la ventana que la configura. Va en la pantalla de "lista" después de
// contratar el plan de una marca, y en cada fila de Configuración → Plan / Facturación. Sólo el dueño
// la ve (son pantallas suyas); el servidor igual rechaza a cualquier otro.

import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ChevronRight, Loader2, X, Zap } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { configurarRecargaDeMarca } from '../../lib/paymentServer';
import { useAperturaAnimada } from '../../hooks/useAperturaAnimada';
import {
  avisoDeLaRecarga,
  detalleDeLaRecarga,
  mensajeDeErrorDeRecarga,
  packsDisponibles,
  pesos,
  precioDePack,
  TOPE_DE_RECARGAS_POR_MES,
  tituloDeLaRecarga,
  UMBRALES_DE_RECARGA,
  vencimientoEnTexto,
  VIGENCIA_DE_RECARGA_DIAS,
  type RecargaDeMarca,
} from './recargaDeMarca';

interface Props {
  brandId: string;
  recarga: RecargaDeMarca;
  devTester: boolean;
  /** La recarga quedó guardada con estos valores. */
  onCambio: (r: RecargaDeMarca) => void;
  /** `tarjeta`: el recuadro de la pantalla de "lista". `linea`: la fila compacta dentro de Plan. */
  variante?: 'tarjeta' | 'linea';
}

export default function RecargaAutomatica({ brandId, recarga, devTester, onCambio, variante = 'tarjeta' }: Props) {
  const [abierto, setAbierto] = useState(false);
  const aviso = avisoDeLaRecarga(recarga);
  const vence = vencimientoEnTexto(recarga);
  // Con la baja del plan agendada no recarga más: el recuadro informa y no abre la configuración.
  const detenida = recarga.detenidaPorBaja === true;
  const encendida = recarga.activa && !detenida;

  return (
    <>
      <button
        type="button"
        onClick={() => setAbierto(true)}
        disabled={detenida}
        className={
          variante === 'tarjeta'
            ? 'flex w-full items-center gap-3 rounded-lg border border-black/[0.08] bg-white p-4 text-left shadow-[0_1px_2px_rgba(16,24,45,0.04)] transition-[background-color,transform] duration-150 ease-out enabled:hover:bg-black/[0.02] enabled:active:scale-[0.99]'
            : 'mt-3 flex w-full items-center gap-3 rounded-md border border-black/[0.07] bg-white/70 px-3 py-2.5 text-left transition-colors duration-150 enabled:hover:bg-black/[0.02]'
        }
      >
        <span
          className={`grid shrink-0 place-items-center rounded-sm ${variante === 'tarjeta' ? 'h-10 w-10' : 'h-8 w-8'} ${
            encendida
              ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary'
              : 'bg-neutral-100 text-neutral-500'
          }`}
        >
          <Zap className={variante === 'tarjeta' ? 'h-5 w-5' : 'h-4 w-4'} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block font-semibold text-brand-black ${variante === 'tarjeta' ? 'text-[15px]' : 'text-[14px]'}`}>
            {tituloDeLaRecarga(recarga)}
          </span>
          <span className="block text-[13px] leading-snug text-black/55">{detalleDeLaRecarga(recarga)}</span>
          {vence && <span className="mt-0.5 block text-[13px] leading-snug text-black/55">{vence}</span>}
          {aviso && (
            <span className="mt-1 flex items-start gap-1.5 text-[13px] leading-snug text-amber-700">
              <AlertTriangle className="mt-[2px] h-3.5 w-3.5 shrink-0" />
              {aviso}
            </span>
          )}
        </span>
        {!detenida && <ChevronRight className="h-4 w-4 shrink-0 text-black/35" />}
      </button>

      <ConfigurarRecarga
        abierto={abierto}
        brandId={brandId}
        recarga={recarga}
        devTester={devTester}
        onCerrar={() => setAbierto(false)}
        onGuardado={(r) => {
          onCambio(r);
          setAbierto(false);
        }}
      />
    </>
  );
}

function ConfigurarRecarga({
  abierto,
  brandId,
  recarga,
  devTester,
  onCerrar,
  onGuardado,
}: {
  abierto: boolean;
  brandId: string;
  recarga: RecargaDeMarca;
  devTester: boolean;
  onCerrar: () => void;
  onGuardado: (r: RecargaDeMarca) => void;
}) {
  const { montado, visible } = useAperturaAnimada(abierto, 150);
  const [activa, setActiva] = useState(recarga.activa);
  const [creditos, setCreditos] = useState(recarga.creditos);
  const [umbral, setUmbral] = useState(recarga.umbral);
  const [editando, setEditando] = useState<'umbral' | 'pack' | null>(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cada vez que se abre, arranca de lo guardado.
  useEffect(() => {
    if (!abierto) return;
    setActiva(recarga.activa);
    setCreditos(recarga.creditos);
    setUmbral(recarga.umbral);
    setEditando(null);
    setError(null);
  }, [abierto, recarga.activa, recarga.creditos, recarga.umbral]);

  // Foco (review Greptile #223): al abrir entra al diálogo, Tab no sale de él y al cerrar vuelve a lo que
  // estaba enfocado antes (el recuadro que lo abrió).
  const panel = useRef<HTMLDivElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!abierto) return;
    focoPrevio.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const id = window.requestAnimationFrame(() => enfocables(panel.current)[0]?.focus());
    return () => {
      window.cancelAnimationFrame(id);
      focoPrevio.current?.focus();
    };
  }, [abierto]);

  if (!montado) return null;

  const cerrar = () => {
    if (!guardando) onCerrar();
  };

  const alTeclear = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      cerrar();
      return;
    }
    if (e.key !== 'Tab') return;
    const lista = enfocables(panel.current);
    if (lista.length === 0) return;
    const primero = lista[0];
    const ultimo = lista[lista.length - 1];
    if (e.shiftKey && document.activeElement === primero) {
      e.preventDefault();
      ultimo.focus();
    } else if (!e.shiftKey && document.activeElement === ultimo) {
      e.preventDefault();
      primero.focus();
    }
  };

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuario no autenticado');
      const r = await configurarRecargaDeMarca({ userId: user.id, brandId, activa, creditos, umbral });
      if (!r.ok) {
        setError(mensajeDeErrorDeRecarga(r.error));
        return;
      }
      onGuardado({
        ...recarga,
        activa: r.recarga.activa,
        creditos: r.recarga.creditos,
        umbral: r.recarga.umbral,
        motivoPausa: null,
      });
    } catch {
      setError(mensajeDeErrorDeRecarga(''));
    } finally {
      setGuardando(false);
    }
  };

  const saliendo = !abierto;
  const packs = packsDisponibles(devTester);
  // Un pack guardado que ya no está en la lista (el de prueba en otra cuenta) igual se muestra elegido.
  const opcionesDePack = packs.includes(creditos) ? packs : [creditos, ...packs];

  return createPortal(
    <div
      className={`fixed inset-0 z-[10050] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm transition-opacity ease-out sm:items-center sm:p-4 ${
        visible ? 'opacity-100 duration-300' : 'opacity-0 duration-150'
      }`}
      onClick={cerrar}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-recarga-automatica"
        ref={panel}
        onKeyDown={alTeclear}
        onClick={(e) => e.stopPropagation()}
        className={`relative flex max-h-[calc(100dvh-32px)] w-full max-w-md flex-col overflow-hidden rounded-t-lg bg-white shadow-[0_24px_64px_rgba(16,24,45,0.22)] transition-[opacity,transform,filter] ease-out sm:rounded-lg ${
          visible
            ? 'opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0'
            : `opacity-0 duration-150 motion-safe:blur-[4px] ${saliendo ? 'motion-safe:-translate-y-3' : 'motion-safe:translate-y-3'}`
        }`}
      >
        <button
          type="button"
          onClick={cerrar}
          disabled={guardando}
          aria-label="Cerrar"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-md text-black/40 transition-colors hover:bg-black/[0.04] hover:text-black disabled:opacity-40"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="overflow-y-auto p-5 sm:p-6">
          <div className="flex items-center justify-between gap-4 pr-8">
            <h3 id="titulo-recarga-automatica" className="text-[17px] font-semibold text-brand-black">
              Recarga automática
            </h3>
            <button
              type="button"
              role="switch"
              aria-checked={activa}
              aria-label={activa ? 'Desactivar la recarga automática' : 'Activar la recarga automática'}
              onClick={() => setActiva((a) => !a)}
              className={`relative h-6 w-11 shrink-0 rounded-full transition-colors duration-150 ${activa ? 'bg-brand-primary' : 'bg-neutral-300'}`}
            >
              <span
                className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-150 ease-out ${
                  activa ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <p className={`mt-4 text-[15px] leading-[26px] transition-opacity duration-150 ${activa ? 'text-brand-black/85' : 'text-black/40'}`}>
            Cuando le queden menos de{' '}
            <Eleccion abierta={editando === 'umbral'} onClick={() => setEditando((e) => (e === 'umbral' ? null : 'umbral'))}>
              {umbral} créditos
            </Eleccion>
            , realiza automáticamente una recarga de{' '}
            <Eleccion abierta={editando === 'pack'} onClick={() => setEditando((e) => (e === 'pack' ? null : 'pack'))}>
              {creditos} créditos
            </Eleccion>
            .
          </p>

          {editando && (
            <div key={editando} className="mt-3 grid grid-cols-2 gap-2 animate-paso-adelante">
              {(editando === 'pack' ? opcionesDePack : [...UMBRALES_DE_RECARGA]).map((n) => {
                const elegida = editando === 'pack' ? n === creditos : n === umbral;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => {
                      if (editando === 'pack') setCreditos(n);
                      else setUmbral(n);
                      setEditando(null);
                    }}
                    className={`rounded-md border px-3 py-2.5 text-left transition-colors duration-150 ${
                      elegida
                        ? 'border-transparent bg-[rgb(var(--brand-primary-rgb)_/_0.085)] text-brand-primary'
                        : 'border-black/[0.08] bg-white text-brand-black/85 hover:bg-black/[0.02]'
                    }`}
                  >
                    <span className="block text-[14px] font-semibold">{n} créditos</span>
                    {editando === 'pack' && <span className="block text-[13px] opacity-70">{pesos(precioDePack(n))}</span>}
                  </button>
                );
              })}
            </div>
          )}

          <p className="mt-4 text-[13px] leading-snug text-black/55">
            Cada recarga cuesta {pesos(precioDePack(creditos))} y se cobra con la tarjeta guardada. Los créditos de cada recarga vencen
            a los {VIGENCIA_DE_RECARGA_DIAS} días. Hasta {TOPE_DE_RECARGAS_POR_MES} recargas por mes.
          </p>

          {error && <p className="mt-3 text-[13px] text-red-600 animate-fade-in">{error}</p>}

          <div className="mt-5 flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={cerrar}
              disabled={guardando}
              className="rounded-md border border-neutral-200 bg-white px-5 py-2.5 text-[15px] font-medium text-brand-black/90 transition-[background-color,transform] duration-150 ease-out hover:bg-neutral-50 enabled:active:scale-[0.96]"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => void guardar()}
              disabled={guardando}
              className="flex items-center justify-center gap-2 rounded-md bg-brand-primary px-5 py-2.5 text-[15px] font-medium text-white transition-[background-color,transform] duration-150 ease-out hover:bg-brand-primary-hover enabled:active:scale-[0.96] disabled:opacity-70"
            >
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Los botones habilitados del diálogo, en orden de tabulación. */
function enfocables(raiz: HTMLElement | null): HTMLElement[] {
  if (!raiz) return [];
  return Array.from(raiz.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'));
}

/** Un valor de la frase que se toca para elegir otro. */
function Eleccion({ abierta, onClick, children }: { abierta: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={abierta}
      className={`rounded-sm px-1.5 py-0.5 font-semibold text-brand-primary underline decoration-dotted underline-offset-4 transition-colors duration-150 ${
        abierta ? 'bg-[rgb(var(--brand-primary-rgb)_/_0.12)]' : 'bg-[rgb(var(--brand-primary-rgb)_/_0.06)] hover:bg-[rgb(var(--brand-primary-rgb)_/_0.1)]'
      }`}
    >
      {children}
    </button>
  );
}
